import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { ISubGroup } from "../../subcategory";
import { FormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import moment from "moment";
import { makeControlForQuestion } from "../../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { cloneDeep } from "lodash-es";
import { BaseURLService } from "../../../../../../shared/util/baseUrl.service";
import { UserInfoService } from "../../../../../user-info.service";
import { ConditionalExpr } from "@angular/compiler";
import { VirtualScrollerComponent } from "../../../../../../shared/virtual-scrolling/virtual-scroller.component";

declare var $: any;

@Component({
    selector: "app-modern-table",
    templateUrl: "./modern-table.component.html",
    styleUrls: ["./modern-table.component.scss"],
    animations: [
        trigger("cardAnimation", [
            state("true", // isCollapsed
            style({
                height: "0",
            })),
            transition("true => false", animate("300ms ease-out")),
            transition("false => true", animate("300ms ease-in")),
        ]),
    ],
    standalone: false
})
export class ModernTableComponent implements OnInit, OnChanges {
  @Output() modernTableScroll = new EventEmitter<any>();

  @ViewChild('scroll', { static: false, read: ElementRef }) modernTableScrollRef: ElementRef;
  @Output() modernTableScrollRefEmitter = new EventEmitter<ElementRef>();

  ngAfterViewInit() {
    this.modernTableScrollRefEmitter.emit(this.modernTableScrollRef);
  }

  @Input() subGroup: ISubGroup; // subgroup containing list of questions for card
  @Input() groupId: string; // the parent group containing the subgroup
  @Input() formGroup: UntypedFormGroup; // main parent form
  @Input() isVisible: boolean;
  @Input() readonly?: boolean;
  @Input() readonlyGlobal?: boolean;
  @Input() deleted?: boolean;
  @Input() labelClick: Function;
  @Input() inspectMode: 'flag' | 'trace' | null;
  @Input() hideEmptyQuestions: boolean;
  @Input() hideClearOverRideQuestionIcon?: boolean;
  @Input() debugMode?: boolean;
  @Input() taxReturnId?: number;
  @Input() sideOpen: boolean;
  @Input() isFullScreenMode?: boolean;
  @Input() inputQuestionId?: number;
  @Input() hideFullScreen?: boolean;

  @Output() loadTrace = new EventEmitter();
  @Output() selectRow = new EventEmitter();
  @Output() refreshTaxReturn = new EventEmitter();
  @Output() addNewRow = new EventEmitter();
  @Output() toggleFullScreen = new EventEmitter();
  @Output() handleAside = new EventEmitter();

  @ViewChild(VirtualScrollerComponent)
  private virtualViewport: VirtualScrollerComponent;

  @ViewChild("tableFilterModal")
  tableFilterModal: ElementRef;

  @Input() columnOrdering = [];
  rowsToDisplay: ISubGroup['rows'] = [];

  questionsById = {};
  columnQuestions = [];
  filterRules = [];
  editableFilterRuleListing = [];
  editableFilterIndex = null;
  filtersById = {};
  filterOperators = [{
    id: "equals",
    name: "equals to"
  }, {
    id: "greater-than",
    name: "greater than"
  }, {
    id: "less-than",
    name: "less than"
  }, {
    id: "contains",
    name: "contains"
  }, {
    id: "not-contains",
    name: "not contains"
  }];
  hasErrors = false;
  isSuperAdmin: boolean;
  isLocked = new Array(this.columnOrdering.length);
  hoveredIndex: number | null = null;
  colIndex: number = 0;
  columnOrderingFiltered = [];
  isTableLocked: boolean;

  constructor(private baseUrl: BaseURLService,
    private userInfo: UserInfoService
  ) {
  }

  ngOnInit(): void {
    this.isSuperAdmin = this.userInfo.isSuperAdmin;
    this.initTable();
    this.initIsLocked();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.debugMode || changes.subGroup) {
      this.initTable()
    }
  }

  initIsLocked() {
    this.columnOrdering.filter(q => !q.hidden && !q.toggleOff).forEach((q, i) => {
      if (q.lockType === 'hardLock' || q.lockType === 'softLock') {
        this.isLocked[i] = true;
        this.columnOrderingFiltered.push(q);
      } else {
        this.isLocked[i] = false;
        this.columnOrderingFiltered.push(q);
      }
    });
    this.isTableLocked = this.subGroup.question.lockType !== 'manualInput';
  }

  initTable() {
    this.rowsToDisplay = this.subGroup.rows;
    this.columnQuestions = this.subGroup.questions.filter(question => !question.hidden && !question.toggleOff);
    this.subGroup.questions.forEach((q,i) => {
      // Question data for filtering options; answers and validators aren't needed
      this.questionsById[q.id] = cloneDeep(q);
      this.questionsById[q.id].answers = [];
      this.questionsById[q.id].mandatory = false;
    });
    this.filterOperators.forEach(f => {
      this.filtersById[f.id] = f;
    });
  }

  newRow(): void {
    this.addNewRow.emit();
    setTimeout(() => {
      this.virtualViewport?.scrollToIndex(this.subGroup.rows.length);
    }, 50);
  }

  trackByRowOrdinal(_index: number, rowAnswer: ISubGroup['rows'][number]) {
    return rowAnswer?.ordinal;
  }

  goToSection(type: any, id: any) {
    const newTab = window.open(`${this.baseUrl.get()}/settings/${type}/${id}`);
    newTab.blur();
    window.focus();
  }

  isReadyOnly() {
    return this.readonly || this.readonlyGlobal || this.deleted;
  }

  doToggleFullScreen() {
    this.isFullScreenMode = !this.isFullScreenMode;
    this.toggleFullScreen.emit({
      requiresFullScreen: this.isFullScreenMode,
      subGroupId: this.subGroup.id,
    });
  }

  openTableFilterModal() {
    this.editableFilterRuleListing = this.filterRules.map(fr => {
      const filter = this.createFilter();
      filter.column.setValue(fr.columnQuestionId);
      filter.operator.setValue(fr.operator);

      const question = this.questionsById[fr.columnQuestionId];
      const valueControl = makeControlForQuestion(question);
      const fieldType = question.displayFieldType.toLocaleLowerCase();
      if (fieldType === "checkbox") {
        const truthiness = String(fr.value).toLocaleLowerCase() === 'true';
        valueControl.setValue(truthiness || false);
      } else {
        valueControl.setValue(fr.value || '');
      }
      filter.formGroup.addControl(String(question.id), valueControl);
      filter.formGroup.addControl("value", valueControl);
      filter.value = valueControl;
      return filter;
    });
    this.editableFilterRuleListing = this.editableFilterRuleListing;
    this.hasErrors = false;
    $(this.tableFilterModal.nativeElement).modal("show");
  }

  updateValueInputForFilter(rowIndex) {
    const filter = this.editableFilterRuleListing[rowIndex];
    const columnQuestionId = filter.column.value;
    const question = this.questionsById[columnQuestionId];
    if (question) {
      filter.formGroup.removeControl(String(question.id));
      filter.formGroup.removeControl("value");

      const valueControl = makeControlForQuestion(question);
      valueControl.setValue('');

      filter.formGroup.addControl(String(question.id), valueControl);
      filter.formGroup.addControl("value", valueControl);
      filter.value = valueControl;
    }
  }

  displayValueForFilter(rowIndex) {
    const filter = this.editableFilterRuleListing[rowIndex];
    if (!filter.column?.value) { return null; }

    const question = this.questionsById[filter.column.value];
    if (!question) { return null; }

    const fieldType = question.displayFieldType.toLocaleLowerCase();
    if (fieldType === "checkbox") {
      const value = filter.value?.value;
      const truthiness = String(value).toLocaleLowerCase() === 'true';
      return String(truthiness);
    }
    return filter.value.value;
  }

  selectFilter(index) {
    // setTimeout() needed as the clickOutside event gets triggered at the same time which causes it to unselect immediately
    setTimeout(() => { this.editableFilterIndex = index; }, 0);
  }

  unselectFilter(index) {
    if (this.editableFilterIndex === index) {
      this.editableFilterIndex = null;
      if (this.hasErrors) {
        this.validateFilterRules();
      }
    }
  }

  removeFilter(index) {
    this.editableFilterRuleListing.splice(index, 1);
    if (this.hasErrors) {
      this.validateFilterRules();
    }
  }

  createFilter() {
    const filterRule = new UntypedFormGroup({});
    const columnControl = new UntypedFormControl('');
    const operatorControl = new UntypedFormControl('');
    const valueControl = new UntypedFormControl('');
    filterRule.addControl("column", columnControl);
    filterRule.addControl("operator", operatorControl);
    filterRule.addControl("value", valueControl);
    return {
      formGroup: filterRule,
      column: columnControl,
      operator: operatorControl,
      value: valueControl,
    };
  }

  addFilter() {
    const newFilter = this.createFilter();
    this.editableFilterRuleListing.push(newFilter);
  }

  resetFilter() {
    this.editableFilterRuleListing = [];
  }

  validateFilterRules() {
    this.editableFilterRuleListing.forEach(fr => {
      fr.hasErrors = false;
    });
    const invalidRules = this.editableFilterRuleListing.filter(fr => {
      const hasColumn = fr.column?.value && this.questionsById[fr.column.value];
      const hasOperator = fr.operator?.value;
      return !hasColumn || !hasOperator;
    });
    invalidRules.forEach(fr => {
      fr.hasErrors = true;
    });
    this.hasErrors = (invalidRules.length > 0);
    return this.hasErrors;
  }

  applyFilter() {
    this.validateFilterRules();
    if (this.hasErrors) {
      return;
    }

    if (this.editableFilterRuleListing.length === 0) {
      this.filterRules = [];
      this.rowsToDisplay = this.subGroup.rows;
      $(this.tableFilterModal.nativeElement).modal("hide");
    }

    const filterRules = this.editableFilterRuleListing.map(fr => ({
      columnQuestionId: fr.column.value,
      operator: fr.operator.value,
      value: fr.value.value,
    }));

    const matchesAllRules = (row) => {
      return filterRules.every(fr => {
        const question = this.questionsById[fr.columnQuestionId];
        const cell = row.cells[fr.columnQuestionId];
        let candidate = cell.answer;
        // cells with unsaved changes get filtered based on the current value in the cell the user can see
        if (cell.answerChanged) {
          const answer = this.formGroup.get(`${question.id}-${row.ordinal}`);
          candidate = answer.value;
        }
        return this.doesCandidateMatchFilter(question, candidate, fr.operator, fr.value);
      });
    };

    this.filterRules = filterRules;
    this.rowsToDisplay = this.subGroup.rows.filter(row => matchesAllRules(row));
    $(this.tableFilterModal.nativeElement).modal("hide");
  }

  doesCandidateMatchFilter(question, candidate, operator, value) {
    const fieldType = question.displayFieldType.toLocaleLowerCase();
    const dataType = question.dataType.toLocaleLowerCase();

    const isDate = (fieldType === 'date picker');
    const isNumber = (fieldType === 'text' || fieldType === 'textarea') && (dataType === 'integer' || dataType === 'float');

    switch (operator) {
      case "equals":
        if (fieldType === 'checkbox') {
          const valueBool = String(value).toLocaleLowerCase() === 'true';
          const candidateBool = String(candidate).toLowerCase() === 'true';
          return candidateBool === valueBool;
        }
        if (isNumber) {
          return Number(candidate) === Number(value);
        }
        return candidate === value;
      case "greater-than":
        if (isDate) {
          return moment(candidate, "DD/MM/YYYY").isAfter(moment(value, "DD/MM/YYYY"));
        }
        if (isNumber) {
          return Number(candidate) > Number(value);
        }
        return false;
      case "less-than":
        if (isDate) {
          return moment(candidate, "DD/MM/YYYY").isBefore(moment(value, "DD/MM/YYYY"));
        }
        if (isNumber) {
          return Number(candidate) < Number(value);
        }
        return false;
      case "contains":
        return candidate.includes(value);
      case "not-contains":
        return !candidate.includes(value);
      default:
        return false;
    }
  }

  handleSideOpen() {
    this.handleAside.emit(this.sideOpen ? !this.sideOpen : true);
  }

  toggleLock(i) {
    this.isLocked[i] = !this.isLocked[i];
  }

  toggleTableLock() {
    if (this.isTableLocked) {
      // Need to unlock table and other columns / ques of same lock type
      this.columnOrderingFiltered.forEach((ques, i) => {
        if (ques.lockType === this.subGroup.question.lockType) {
          this.isLocked[i] = false;
        }
      })
    } else {
      this.columnOrderingFiltered.forEach((ques, i) => {
        if (ques.lockType === this.subGroup.question.lockType) {
          this.isLocked[i] = true;
        }
      })
    }
    this.isTableLocked = !this.isTableLocked
  }
}
